import { useAuthStore } from "@/stores/auth-store";
import { getApiUrl } from "@/lib/api/utils";

const AUTH_DECISION_PREFIX = "[AuthDecision]";

/** Temporary diagnostics for auth redirect / cookie / persisted state bugs */
export function logAuthDecision(message: string, extra?: Record<string, unknown>) {
  if (typeof console !== "undefined" && console.warn) {
    console.warn(`${AUTH_DECISION_PREFIX} ${message}`, extra ?? {});
  }
}

export type AuthShellMode = "web" | "capacitor-native";

/**
 * How we treat the shell for auth redirects. Only `capacitor-native` skips the web cookie gate.
 * IMPORTANT: Do not use `typeof window.Capacitor !== "undefined"` — extensions/dev stubs can define
 * `window.Capacitor` on localhost while still being a normal browser (causes login ↔ dashboard loops).
 */
export function getAuthShellMode(): AuthShellMode {
  if (typeof window === "undefined") return "web";
  const cap = (window as Window & {
    Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string };
  }).Capacitor;
  if (!cap) return "web";
  if (typeof cap.isNativePlatform === "function") {
    return cap.isNativePlatform() ? "capacitor-native" : "web";
  }
  const platform = cap.getPlatform?.();
  if (platform === "ios" || platform === "android") return "capacitor-native";
  return "web";
}

/** For [AuthDecision] logs: explains web vs native classification */
export function getAuthShellDebug(): Record<string, unknown> {
  if (typeof window === "undefined") {
    return { shell: "web", detail: "no-window" };
  }
  const cap = (window as Window & {
    Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string };
  }).Capacitor;
  if (!cap) {
    return { shell: "web", detail: "no-Capacitor-global" };
  }
  const mode = getAuthShellMode();
  const platform = cap.getPlatform?.();
  const nativeFn = cap.isNativePlatform;
  const nativeResult = typeof nativeFn === "function" ? nativeFn() : undefined;
  return {
    shell: mode,
    capacitorGetPlatform: platform,
    capacitorIsNativePlatform: nativeResult,
    detail:
      mode === "web"
        ? "Capacitor global present but not native iOS/Android shell (web build, dev stub, or extension)"
        : "Native Capacitor app (iOS/Android)",
  };
}

export function isCapacitorRuntime(): boolean {
  return getAuthShellMode() === "capacitor-native";
}

/**
 * When persisted client auth claims logged-in but HTTP cookies are not valid for SSR/middleware,
 * clear Supabase client storage + Zustand so we cannot loop login ↔ dashboard.
 */
/**
 * Signing somebody out needs positive evidence that the session is gone.
 *
 * `verifyWebCookieSessionWithRetry` returns `unknown` when it never got an answer at all: the
 * network dropped, the tab was backgrounded, the request was aborted mid-navigation, or the 4s
 * timeout won. Every caller used to treat that the same as `no_session` and clear the session, so a
 * momentary blip signed a perfectly valid member out and threw them back to the login page. That is
 * what happened to Tom on 2026-08-16: the server logged a successful sign-in, a successful cookie
 * check, the dashboard and a run of authenticated API calls, and then the login page again. The
 * probe that made the decision never reached the server, which is exactly why nothing explained it.
 *
 * `unknown` means "we could not ask", and that is not an answer. Leave the session alone; a later
 * probe, or a real 401 from the API, will settle it with actual evidence.
 */
export async function clearClientAuthIfServerSaysSignedOut(
  cookieResult: WebCookieSessionResult,
  reason: string,
): Promise<void> {
  if (cookieResult === "no_session") {
    await clearStaleWebAuthCookieDesync(reason);
    return;
  }
  logAuthDecision(
    "Keeping client auth: could not reach the cookie session, which is not proof of being signed out",
    { reason, cookieResult },
  );
}

export async function clearStaleWebAuthCookieDesync(reason: string): Promise<void> {
  logAuthDecision("Clearing stale client auth (cookie session invalid vs persisted state)", {
    reason,
  });
  try {
    const { supabase, isSetSessionInProgress } = await import("@/lib/supabase/client");
    // CRITICAL: signOut() aborts in-flight GoTrue token requests. If this runs while email login is
    // calling setSession (persisted Zustand + no local refresh token yet → cookie probe fails),
    // the user sees AbortError / "Session setup was interrupted". Skip until session is established.
    if (isSetSessionInProgress()) {
      logAuthDecision(
        "Skipped clearStaleWebAuthCookieDesync — setSession guard active (avoid aborting login)",
        { reason },
      );
      return;
    }
    await supabase.auth.signOut().catch(() => {});
  } catch {
    /* non-fatal */
  }
  try {
    useAuthStore.setState({
      user: null,
      isAuthenticated: false,
      membershipTier: "free",
    });
    const { useProfileStore } = await import("@/stores/profile-store");
    useProfileStore.setState({ currentProfile: null });
  } catch {
    /* non-fatal */
  }
}

export type WebCookieSessionResult = "ok" | "no_session" | "unknown";

export type VerifyWebCookieSessionOptions = {
  maxAttempts?: number;
  delayBetweenAttemptsMs?: number;
};

/**
 * Bounded retries. Returns `unknown` only when no successful HTTP response was obtained (fail-safe: do not assume logged in).
 */
export async function verifyWebCookieSessionWithRetry(
  options?: VerifyWebCookieSessionOptions,
): Promise<WebCookieSessionResult> {
  const attempts = options?.maxAttempts ?? 3;
  const delayMs = options?.delayBetweenAttemptsMs ?? 120;
  let lastError: unknown;
  for (let i = 0; i < attempts; i++) {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 4000);
    try {
      const url = getApiUrl("/api/auth/cookie-session");
      const res = await fetch(url, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
        signal: controller.signal,
      });
      clearTimeout(t);
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean };
      const ok = Boolean(data?.ok);
      logAuthDecision(i === 0 ? "Cookie-session probe" : "Cookie-session probe (retry)", {
        attempt: i + 1,
        httpOk: res.ok,
        sessionOk: ok,
      });
      return ok ? "ok" : "no_session";
    } catch (e) {
      clearTimeout(t);
      lastError = e;
      logAuthDecision("Cookie-session probe failed (network/abort)", {
        attempt: i + 1,
        error: e instanceof Error ? e.message : String(e),
      });
      if (i < attempts - 1) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
  }
  logAuthDecision("Cookie-session probe exhausted", {
    lastError: lastError instanceof Error ? lastError.message : String(lastError),
  });
  return "unknown";
}

/**
 * After optimistic navigation, sync Zustand + Supabase and re-probe cookies so SSR/middleware
 * converge without blocking launch UI.
 */
function scheduleBackgroundCookieAlignment(): void {
  if (typeof window === "undefined") return;
  void (async () => {
    try {
      const { useAuthStore } = await import("@/stores/auth-store");
      await Promise.race([
        useAuthStore.getState().syncSession(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("syncSession timeout")), 12000),
        ),
      ]).catch(() => {});
      const { supabase } = await import("@/lib/supabase/client");
      await supabase.auth.getSession().catch(() => {});
      await verifyWebCookieSessionWithRetry({
        maxAttempts: 4,
        delayBetweenAttemptsMs: 180,
      });
    } catch {
      /* non-fatal */
    }
  })();
}

/**
 * Ensures Supabase SSR cookies match middleware before navigating to protected routes.
 *
 * Mainstream-app behavior: if a refresh token exists locally (Supabase persisted session),
 * do not block the UI on cookie probes or long sync — treat as OK and align in the background.
 * Cookie/session HTTP checks can lag localStorage + Capacitor Preferences on cold start; clearing
 * client auth on a failed probe caused false logouts.
 */
export async function alignServerAuthCookiesBeforeAppEntry(): Promise<WebCookieSessionResult> {
  const native = isCapacitorRuntime();

  if (typeof window !== "undefined") {
    try {
      const { sessionRestorePromise, hasStoredRefreshToken } = await import("@/lib/supabase/client");
      await Promise.race([
        sessionRestorePromise,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("sessionRestore timeout")), native ? 8000 : 5000),
        ),
      ]).catch(() => {});
      await new Promise((r) => setTimeout(r, native ? 80 : 0));

      // Native cold start: Preferences → localStorage may finish inside sessionRestorePromise.
      // If we already have a refresh token, skip blocking cookie verification entirely.
      if (native && hasStoredRefreshToken()) {
        logAuthDecision(
          "alignServerAuthCookies: native optimistic OK — local refresh token present (background cookie sync)",
          getAuthShellDebug(),
        );
        scheduleBackgroundCookieAlignment();
        return "ok";
      }
    } catch {
      /* non-fatal */
    }
  }

  try {
    const { useAuthStore } = await import("@/stores/auth-store");
    await Promise.race([
      useAuthStore.getState().syncSession(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("syncSession timeout")), native ? 4000 : 3000),
      ),
    ]).catch(() => {});
  } catch {
    /* non-fatal */
  }

  try {
    const { supabase } = await import("@/lib/supabase/client");
    await supabase.auth.getSession();
  } catch {
    /* non-fatal — nudge @supabase/ssr cookie sync */
  }

  const { hasStoredRefreshToken } = await import("@/lib/supabase/client");

  const probe = await verifyWebCookieSessionWithRetry({
    maxAttempts: native ? 5 : 3,
    delayBetweenAttemptsMs: native ? 180 : 120,
  });

  if (probe === "ok") {
    return "ok";
  }

  // Local Supabase session exists but cookie probe failed or network was flaky — same as other
  // installed apps: stay signed in, sync cookies after paint.
  if (hasStoredRefreshToken()) {
    logAuthDecision(
      "alignServerAuthCookies: optimistic OK — cookie probe not ok but local refresh token present",
      { ...getAuthShellDebug(), probe },
    );
    scheduleBackgroundCookieAlignment();
    return "ok";
  }

  return probe;
}
